import {
  type ReactElement,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useSyncExternalStore,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { ApiClient } from '../api/client.js';
import { type ConnectivityStore, createConnectivityStore, isDisconnected } from './connectivity.js';
import type { QueuedWrite } from './db.js';
import { OfflineContext, type OfflineContextValue } from './offline-context.js';
import { type EnqueueInput, WriteQueue } from './write-queue.js';

/**
 * Owns the two pieces of offline machinery — the connectivity detector and the durable write
 * queue — and joins them: when the connection comes back, the queue flushes; when a queued
 * write lands, the caches it names are invalidated so the screen catches up on its own.
 *
 * Both dependencies are injectable so this can be tested without a browser.
 */
export interface OfflineProviderProps {
  client: ApiClient;
  children: ReactNode;
  /** Test seam. */
  connectivity?: ConnectivityStore;
  /** Test seam. */
  queue?: WriteQueue;
}

export function OfflineProvider({
  client,
  children,
  connectivity: injectedConnectivity,
  queue: injectedQueue,
}: OfflineProviderProps): ReactElement {
  const queryClient = useQueryClient();

  const connectivity = useMemo(
    () => injectedConnectivity ?? createConnectivityStore(),
    [injectedConnectivity],
  );

  // Held in a ref so the queue's callbacks always see the current query client without the
  // queue itself being rebuilt (rebuilding it would drop its subscribers mid-flush).
  const queryClientRef = useRef(queryClient);
  queryClientRef.current = queryClient;

  const queue = useMemo(() => {
    if (injectedQueue) return injectedQueue;
    return new WriteQueue({
      send: (write: QueuedWrite) =>
        client.request({
          method: write.method,
          path: write.path,
          body: write.body,
          // The server dedupes on this, so a write delivered twice after a flaky reconnect
          // does not become two payments.
          headers: { 'Idempotency-Key': write.id },
        }),
      onSent: (write) => {
        for (const key of write.invalidate ?? []) {
          void queryClientRef.current.invalidateQueries({ queryKey: key });
        }
      },
    });
  }, [client, injectedQueue]);

  const status = useSyncExternalStore(connectivity.subscribe, connectivity.getSnapshot, () =>
    connectivity.getSnapshot(),
  );

  const snapshot = useSyncExternalStore(
    (listener) => queue.subscribe(listener),
    () => queue.getSnapshot(),
    () => queue.getSnapshot(),
  );

  const flush = useCallback(async () => {
    const result = await queue.flush();
    // A flush is the most honest connectivity probe there is: it used the real endpoint.
    if (result.interrupted) connectivity.reportOutcome(false);
    else if (result.sent > 0) connectivity.reportOutcome(true);
    return result;
  }, [connectivity, queue]);

  // Rehydrate whatever survived the last session, and send it if we can.
  useEffect(() => {
    void queue.refresh();
  }, [queue]);

  useEffect(() => {
    if (isDisconnected(status)) return;
    if (snapshot.pendingCount === 0) return;
    void flush();
  }, [flush, snapshot.pendingCount, status]);

  useEffect(() => {
    return () => {
      if (!injectedConnectivity) connectivity.destroy();
    };
  }, [connectivity, injectedConnectivity]);

  const value = useMemo<OfflineContextValue>(
    () => ({
      status,
      isOffline: isDisconnected(status),
      pending: snapshot.pending,
      failed: snapshot.failed,
      pendingCount: snapshot.pendingCount,
      failedCount: snapshot.failedCount,
      flushing: snapshot.flushing,
      enqueue: (input: EnqueueInput) => queue.enqueue(input),
      flush,
      discard: (seq: number) => queue.discard(seq),
      retry: (seq: number) => queue.retry(seq),
    }),
    [flush, queue, snapshot, status],
  );

  return <OfflineContext.Provider value={value}>{children}</OfflineContext.Provider>;
}
